import { useContext, useEffect, useRef, useState } from "react";

import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { AppContext } from "App";
import Axios from "axios";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { enqueueSnackbar } from "notistack";

import { UsersApiHelperForMember } from "../../api/ApiHelper";

export const AgencyAddEditDialog = ({ onChange, onClose, addEditAgency }) => {
  const [state, setState] = useState(() => (addEditAgency?.id ? { ...addEditAgency } : { name: "" }));
  const { component: formStatusComponent } = useFormStatus();
  const { globalState } = useContext(AppContext);
  const fileInputRef = useRef(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  useEffect(() => {
    if (addEditAgency && addEditAgency.id) {
      // edit: include existing name
      setState({ ...addEditAgency });
    } else {
      // new: reset name only
      setState({ name: "" });
    }
  }, [addEditAgency]);

  const handleChangeState = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!state.name?.trim()) {
      enqueueSnackbar("Agency Name is required.", { variant: "error" });
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    if (!validateForm()) return;
    onChange?.(state);
    onClose?.();
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    confirmDialogActions.confirm({
      title: "Upload Logo",
      prompt: "Upload this image as the agency logo?",
      promptDetail: file.name,
      callback: () => handleLogoUpload(file),
    });
  };

  const handleLogoUpload = async (file) => {
    setIsUploadingLogo(true);
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      const { url: presignedUrl, logo_url: freshLogoUrl } = await UsersApiHelperForMember.callPost(
        "agency/AgencyLogo/Update",
        { id: state.id, file_extension: ext },
        globalState
      );

      await Axios.put(presignedUrl, file, {
        timeout: 60 * 1000,
        headers: { "Content-Type": file.type },
      });

      const updatedState = { ...state, logo_url: freshLogoUrl ?? `assets-logos/${state.id}/logo.${ext}` };
      setState(updatedState);
      onChange?.(updatedState);
    } catch {
      enqueueSnackbar("Failed to upload logo. Please try again.", { variant: "error" });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return (
    <>
      <DialogTitle>{addEditAgency?.id ? "Edit Agency" : "Create Agency"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth>
              <TextField
                label="Agency Name"
                name="name"
                value={state.name}
                required
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>

          {state.id && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Agency Logo
              </Typography>
              <Box
                onClick={isUploadingLogo ? undefined : handleLogoClick}
                sx={{
                  width: 160,
                  height: 100,
                  border: "2px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isUploadingLogo ? "default" : "pointer",
                  overflow: "hidden",
                  "&:hover": isUploadingLogo ? {} : { borderColor: "primary.main" },
                }}
              >
                {isUploadingLogo ? (
                  <CircularProgress size={32} />
                ) : state.logo_url ? (
                  <img
                    src={`${state.logo_url}?key=${Date.now()}`}
                    alt="Agency logo"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={onSubmit}>
            OK
          </Button>
          <Button variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
      {formStatusComponent}
      {confirmDialogComponent}
    </>
  );
};
